import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '../../global/button/Button'
import IconButton from '../../global/iconButton/IconButton'
import { CircleCloseLineIcon } from '../../icons/CircleCloseLineIcon'
import { CirclePlaceholderIcon } from '../../icons/CirclePlaceholderIcon'
import { SearchIcon } from '../../icons/SearchIcon'
import { useWindowResize } from '../../utils/domUtils'
import { Input } from '../v2/Input/Input'
import { FullPageModalMobile } from '../v2/Modal/FullPageModalMobile'

import styles from './SearchField.module.scss'

export interface SearchFieldProps {
  placeholder?: string
  onChange: (value: string) => void
  onSearch?: () => void
  value: string | null
  disabled?: boolean
  className?: string
  canClear?: boolean
  mobileModal?: boolean
  /**
   * Used for rendering search results in mobile modal.
   */
  renderResult?: () => React.ReactNode
}

export const SearchField: React.FC<SearchFieldProps> = (props) => {
  const { placeholder, value, disabled, mobileModal, canClear, className } = props
  const { onChange, onSearch, renderResult } = props
  const { t } = useTranslation()
  const isMobile = useWindowResize()

  const [showModal, setShowModal] = useState(false)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)
  }

  const handleClear = () => {
    if (canClear) {
      onChange('')
    }
  }

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        onSearch?.()
      }
    }

    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [onSearch])

  if (isMobile && mobileModal) {
    return (
      <>
        {showModal && renderResult?.() && (
          <FullPageModalMobile
            onClose={() => setShowModal(false)}
            goBack={() => setShowModal(false)}
          >
            <div className={styles.searchFieldContainer}>
              <Input
                placeholder={placeholder ?? t('Search')}
                startIcon={SearchIcon}
                endIcon={
                  canClear && (value?.length ?? 0) > 0 ? CircleCloseLineIcon : CirclePlaceholderIcon
                }
                onEndIconClick={handleClear}
                onChange={handleChange}
                value={value ?? ''}
                disabled={disabled}
                className={className}
              />
              <Button.New appearance='plain' size='S' onClick={() => setShowModal(false)}>
                {t('Cancel')}
              </Button.New>
            </div>
            {renderResult?.()}
          </FullPageModalMobile>
        )}

        <IconButton.New
          appearance='secondary'
          size='M'
          className={styles.downloadButton}
          onClick={() => setShowModal(true)}
        >
          <SearchIcon />
        </IconButton.New>
      </>
    )
  }
  return (
    <Input
      placeholder={placeholder ?? t('Search')}
      startIcon={SearchIcon}
      endIcon={canClear && (value?.length ?? 0) > 0 ? CircleCloseLineIcon : CirclePlaceholderIcon}
      onEndIconClick={handleClear}
      onChange={handleChange}
      value={value ?? ''}
      disabled={disabled}
      className={className}
    />
  )
}
